import { CSSProperties, useMemo } from 'react'

import productUrl from 'src/functions/productUrl'
import getProductImageUrl from 'src/pages/Product/functions/imageUrl'
import ShopLogo from 'template/logos/logo.svg'
import shopInfos from 'template/infos'
import {
  MagazineImageCaptionPosition,
  MagazineImageLayout,
  MagazineOverlayPosition,
  MagazinePage,
  MagazineTextPosition,
} from 'src/shared/dbTypes/magazine'
import { publicMagazineResponse } from 'src/shared/apiBackendResponses/magazine'
import { tProductPreview } from 'src/shared/frontendTypes/product'

import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import {
  createLandingPageImageAiGeneratedMatcher,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'
import useStyles from './MagazineArticle.styles'

const normalizeMagazineImageUrl = (value?: string) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('/api/media/landingpages/')) return trimmed.replace('/api/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/media/landingpages/')) return trimmed.replace('/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/images/landingpages/')) return trimmed.replace('/images/landingpages/', '/api/images/landingpages/')
  return trimmed
}

const pageClassMap = {
  coverGrid: 'templateCoverGrid',
  editorialIndex: 'templateEditorialIndex',
  splitArticle: 'templateSplitArticle',
  imageEssay: 'templateImageEssay',
  heroFeature: 'templateHeroFeature',
  productEditorial: 'templateProductEditorial',
  productImage: 'templateProductImage',
  collage: 'templateCollage',
  pinboard: 'templatePinboard',
  faqText: 'templateFaqText',
} as const

const imageLayoutClassMap: Record<MagazineImageLayout, keyof ReturnType<typeof useStyles>> = {
  standard: 'imageStandard',
  wide: 'imageWide',
  fullscreen: 'imageFullscreen',
  hero: 'imageHero',
  portrait: 'imagePortrait',
  inset: 'imageInset',
  circle: 'imageCircle',
  cutout: 'imageCutout',
}

const templateImageLayoutSlots: Record<MagazinePage['template'], MagazineImageLayout[]> = {
  coverGrid: ['wide', 'standard', 'standard', 'standard'],
  editorialIndex: ['hero', 'inset'],
  splitArticle: ['portrait', 'standard'],
  imageEssay: ['fullscreen', 'inset'],
  heroFeature: ['fullscreen'],
  productEditorial: ['wide', 'inset'],
  productImage: ['wide'],
  collage: ['cutout', 'circle', 'portrait', 'standard'],
  pinboard: ['portrait', 'standard', 'wide', 'circle', 'inset'],
  faqText: [],
}

const getTemplateImageLayout = (
  template: MagazinePage['template'],
  index: number,
  fallback?: MagazineImageLayout,
) => templateImageLayoutSlots[template]?.[index] || fallback || 'standard'

const allowsImageFineTuning = (template: MagazinePage['template']) =>
  template === 'collage' || template === 'pinboard'

const captionPositionClassMap: Record<MagazineImageCaptionPosition, keyof ReturnType<typeof useStyles>> = {
  auto: 'captionAuto',
  insideBottom: 'captionAuto',
  insideTop: 'captionInsideTop',
  outside: 'captionOutside',
  hidden: 'captionHidden',
}

const overlayPositionClassMap: Record<MagazineOverlayPosition, keyof ReturnType<typeof useStyles>> = {
  topLeft: 'overlayTopLeft',
  topCenter: 'overlayTopCenter',
  topRight: 'overlayTopRight',
  middleLeft: 'overlayMiddleLeft',
  middleCenter: 'overlayMiddleCenter',
  middleRight: 'overlayMiddleRight',
  bottomLeft: 'overlayBottomLeft',
  bottomCenter: 'overlayBottomCenter',
  bottomRight: 'overlayBottomRight',
}

const textPositionClassMap: Record<MagazineTextPosition, keyof ReturnType<typeof useStyles>> = {
  auto: 'textAuto',
  topLeft: 'textTopLeft',
  topRight: 'textTopRight',
  center: 'textCenter',
  bottomLeft: 'textBottomLeft',
  bottomRight: 'textBottomRight',
  leftColumn: 'textLeftColumn',
  rightColumn: 'textRightColumn',
}

const getProductImage = (product: tProductPreview) => product.images?.[0]
const getProductTileImageUrl = (product: tProductPreview) => {
  const image = getProductImage(product)
  if (!image) return ''

  return getProductImageUrl(image, product.id, 'sm')
    || getProductImageUrl(image, product.id, 'xs')
    || getProductImageUrl(image, product.id, 'md')
    || ''
}

type MagazineTextField = 'kicker' | 'title' | 'subtitle' | 'body' | 'quote'

const getMagazineTextStyle = (page: MagazinePage, field: MagazineTextField): CSSProperties => {
  const style = page.textStyles?.[field]
  return {
    ...(style?.fontFamily ? { fontFamily: style.fontFamily } : {}),
    ...(style?.fontSize ? { fontSize: `${style.fontSize}px` } : {}),
    ...(style?.fontWeight ? { fontWeight: style.fontWeight } : {}),
    ...(style?.color ? { color: style.color } : {}),
  }
}

const renderParagraphs = (text?: string) =>
  (text || '')
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean)
    .map((part, index) => <p key={index}>{part}</p>)

const getStaticImageSrc = (image: unknown) => {
  if (typeof image === 'string') return image
  if (image && typeof image === 'object' && 'src' in image) {
    const src = (image as { src?: unknown }).src
    return typeof src === 'string' ? src : ''
  }
  return ''
}

const shopLogoSrc = getStaticImageSrc(ShopLogo)
const MAGAZINE_PAGE_HEIGHT = 620

interface Props {
  magazine: publicMagazineResponse
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const MagazineArticle: React.FC<Props> = ({ magazine, landingPageImageAiGenerated }) => {
  const classes = useStyles()
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated]
  )
  const isProductImageMode = (magazine.mode || 'magazine') === 'productImages'
  const pages = isProductImageMode
    ? (magazine.pages || []).filter(page => page.template === 'productImage')
    : (magazine.pages || [])
  const productsById = new Map((magazine.products || []).map(product => [product.id, product]))
  const productsByKey = new Map((magazine.products || []).map(product => [product.key, product]))

  const getProductForRef = (ref: { productId: number; productKey?: string }) =>
    (ref.productKey ? productsByKey.get(ref.productKey) : undefined)
    || productsById.get(ref.productId)

  const renderProductTile = (ref: MagazinePage['products'][number], index: number) => {
    const product = getProductForRef(ref)
    if (!product) return null

    const imageUrl = getProductTileImageUrl(product)

    return <a
      key={`${product.id}-${product.key}-${index}`}
      className={classes.productTile}
      href={productUrl(product.id, product.path, product.options)}
    >
      {imageUrl && <span className={classes.productTileImage}>
        <img src={imageUrl} alt={product.metaTitle} />
        <AiGeneratedBadge aiGenerated={getProductImage(product)?.aiGenerated} size='compact' />
      </span>}
      <strong>{ref.title || product.metaTitle}</strong>
      {(ref.subtitle || product.brandTitel) && <span>{ref.subtitle || product.brandTitel}</span>}
    </a>
  }

  const renderPage = (page: MagazinePage, index: number) => {
    const pageImages = (page.images || []).filter(image => image.src)
    const images = pageImages.length > 0
      ? pageImages
      : index === 0 && magazine.coverImage?.src
        ? [{ ...magazine.coverImage, layout: magazine.coverImage.layout || 'wide' }]
        : []
    const pageHeight = Math.max(100, page.heightVh || 100)
    const hideEditorialText = isProductImageMode && page.template === 'productImage'
    const pageStyle: CSSProperties & Record<'--magazine-accent' | '--magazine-page-height', string> = {
      '--magazine-accent': page.accentColor || '#8f947b',
      '--magazine-page-height': `${Math.round(MAGAZINE_PAGE_HEIGHT * pageHeight / 100)}px`,
    }
    if (page.backgroundColor) pageStyle.backgroundColor = page.backgroundColor
    if (page.textColor) pageStyle.color = page.textColor

    const pageLogoSrc = page.logo?.source === 'shop'
      ? shopLogoSrc
      : page.logo?.source === 'media'
        ? normalizeMagazineImageUrl(page.logo.src)
        : ''

    return <article
      id={`seite-${index + 1}`}
      key={page.id}
      className={[
        classes.page,
        classes[pageClassMap[page.template]],
        classes[textPositionClassMap[page.textPosition || 'auto']],
      ].join(' ')}
      style={pageStyle}
    >
      {!hideEditorialText && page.kicker && <div className={classes.pageKicker} style={getMagazineTextStyle(page, 'kicker')}>{page.kicker}</div>}
      {!hideEditorialText && page.title && <h2 className={classes.pageTitle} style={getMagazineTextStyle(page, 'title')}>{page.title}</h2>}
      {!hideEditorialText && page.subtitle && <div className={classes.pageSubtitle} style={getMagazineTextStyle(page, 'subtitle')}>{page.subtitle}</div>}
      {!hideEditorialText && page.body && <div className={classes.pageBody} style={getMagazineTextStyle(page, 'body')}>{renderParagraphs(page.body)}</div>}
      {!hideEditorialText && page.quote && <blockquote style={getMagazineTextStyle(page, 'quote')}>{page.quote}</blockquote>}
      {pageLogoSrc && page.logo &&
        <div
          className={[
            classes.pageMark,
            classes[overlayPositionClassMap[page.logo.position || 'topRight']],
          ].join(' ')}
          style={{ ['--magazine-logo-width' as string]: `${page.logo.width || 180}px` } as CSSProperties}
        >
          <img src={pageLogoSrc} alt={page.logo.alt || shopInfos.name || magazine.title} />
          <AiGeneratedBadge
            aiGenerated={page.logo.aiGenerated || isAiGeneratedLandingImage(page.logo.src)}
            size='compact'
            placement='top-left'
          />
        </div>
      }
      {page.textLine?.text &&
        <div
          className={[
            classes.pageTextLine,
            classes[overlayPositionClassMap[page.textLine.position || 'bottomLeft']],
          ].join(' ')}
        >
          {page.textLine.text}
        </div>
      }
      {images.length > 0 &&
        <div className={classes.imageGrid}>
          {images.map((image, imageIndex) => {
            const slotLayout = getTemplateImageLayout(page.template, imageIndex, image.layout || 'standard')
            const useFineTuning = allowsImageFineTuning(page.template)

            return <figure
              key={imageIndex}
              className={[
                classes[imageLayoutClassMap[slotLayout]],
                classes[captionPositionClassMap[image.captionPosition || 'auto']],
              ].join(' ')}
              style={{
                ['--magazine-image-overlap-x' as string]: `${useFineTuning ? image.overlapX || 0 : 0}px`,
                ['--magazine-image-overlap-y' as string]: `${useFineTuning ? image.overlapY || 0 : 0}px`,
                ['--magazine-image-scale' as string]: `${useFineTuning ? Math.max(20, image.scale || 100) / 100 : 1}`,
              } as CSSProperties}
            >
              <img src={normalizeMagazineImageUrl(image.src)} alt={image.alt || ''} />
              <AiGeneratedBadge
                aiGenerated={image.aiGenerated || isAiGeneratedLandingImage(image.src)}
                placement='top-right'
              />
              {(image.hotspots || []).map((hotspot, hotspotIndex) => {
                const product = getProductForRef(hotspot)
                const title = hotspot.label || product?.metaTitle || `Produkt ${hotspot.productId || ''}`.trim()
                const hotspotStyle = { left: `${hotspot.x ?? 50}%`, top: `${hotspot.y ?? 50}%` }

                return product
                  ? <a
                    key={hotspotIndex}
                    className={classes.imageHotspot}
                    href={productUrl(product.id, product.path, product.options)}
                    style={hotspotStyle}
                    title={title}
                  >
                    <span>{title}</span>
                  </a>
                  : <span
                    key={hotspotIndex}
                    className={classes.imageHotspot}
                    style={hotspotStyle}
                    title={title}
                  />
              })}
              {image.caption && image.captionPosition !== 'hidden' && <figcaption>{image.caption}</figcaption>}
            </figure>
          })}
        </div>
      }
      {(page.products || []).length > 0 &&
        <div className={classes.products}>
          {(page.products || []).map(renderProductTile)}
        </div>
      }
    </article>
  }

  return <main className={classes.root}>
    <header className={classes.hero}>
      <div>
        {(magazine.issue || pages.length > 0) &&
          <div className={classes.heroMeta}>
            {[magazine.issue, `${pages.length} ${isProductImageMode ? 'Bilder' : 'Seiten'}`].filter(Boolean).join(' / ')}
          </div>
        }
        <h1 className={classes.heroTitle}>{magazine.title}</h1>
        {(magazine.subtitle || magazine.excerpt) &&
          <div className={classes.heroCopy}>
            {magazine.subtitle || magazine.excerpt}
          </div>
        }
      </div>
    </header>
    <div className={classes.pages}>
      {pages.map(renderPage)}
    </div>
  </main>
}

export default MagazineArticle
